<template>
  <div class="b-select" :class="{ 'is-open': isOpen, 'is-disabled': disabled }" ref="root" @keydown="onKeydown">
    <p class="b-select__label" v-if="label">
      {{ label }}
      <span v-if="required" class="b-select__required">*</span>
    </p>
    <button
      :class="['b-select__control', { 'b-select__control--error': error }]"
      type="button"
      :disabled="disabled"
      :aria-expanded="String(isOpen)"
      aria-haspopup="listbox"
      @click="toggle"
    >
      <span class="b-select__values">
        <template v-if="hasValue">
          <template v-for="opt in selectedOptions">
            <slot
              name="value"
              :option="opt.raw"
              :label="labelResolver(opt)"
              :value="valueResolver(opt)"
              :placeholder="placeholder"
              :node-key="opt._key"
            >
              <span class="b-select__label text-truncate" :key="opt._key">{{ labelResolver(opt) }}</span>
            </slot>
          </template>
        </template>
        <template v-else>
          <span class="b-select__placeholder">{{ placeholder }}</span>
        </template>
      </span>

      <SelectArrowSvg class="b-select__chevron" aria-hidden="true"></SelectArrowSvg>
    </button>
    <p class="b-select__error" v-if="error">{{ error }}</p>
    <p class="b-select__hint" v-else-if="hint">{{ hint }}</p>

    <transition name="fade">
      <div v-show="isOpen" class="b-select__dropdown">
        <slot name="dropdown-header" :close="close" />

        <ul class="b-select__list" role="listbox">
          <li
            v-for="opt in normalizedOptions"
            :key="opt._key"
            class="b-select__option"
            :class="{
              'is-selected': isSelected(opt.raw),
              'is-disabled': isOptionDisabled(opt.raw),
            }"
            role="option"
            :aria-selected="String(isSelected(opt.raw))"
            :id="opt._key"
            @click="setOption(opt.raw)"
          >
            <slot
              name="option"
              :option="opt.raw"
              :label="labelResolver(opt)"
              :value="valueResolver(opt)"
              :is-selected="isSelected(opt.raw)"
              :is-disabled="isOptionDisabled(opt.raw)"
            >
              <span class="b-select__option-label">{{ labelResolver(opt) }}</span>
            </slot>
          </li>

          <li v-if="!normalizedOptions.length" class="b-select__empty">
            <slot name="empty"></slot>
          </li>
        </ul>

        <slot name="dropdown-footer" :close="close" />
      </div>
    </transition>
  </div>
</template>

<script>
import SelectArrowSvg from '@/public/dist/img/svg/select-arrow.svg?inline'

export default {
  name: 'BaseSelect',
  components: {
    SelectArrowSvg,
  },
  props: {
    value: { type: [Number, String, Array] },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    label: { type: String },
    placeholder: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    labelResolver: { type: Function, default: $ => $?.label },
    valueResolver: { type: Function, default: $ => $?.value },
    keyResolver: { type: [String, Function], default: null },
    optionDisabled: { type: [String, Function], default: 'disabled' },
    closeOnSelect: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    error: { type: String, default: '' },
    hint: { type: String, default: '' },
  },
  data() {
    return {
      isOpen: false,
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick, true)
  },
  computed: {
    normalizedValues() {
      if (this.multiple) {
        return this.value?.length ? this.value : []
      }

      return [this.value]
    },

    normalizedOptions() {
      return (this.options || []).map((raw, i) => {
        const label = this.labelResolver(raw)
        const value = this.valueResolver(raw)
        const _key = this.keyResolver ? this.resolve(raw, this.keyResolver) : value !== undefined ? `v:${String(value)}` : `i:${i}`
        return { raw, label: String(label ?? ''), value, _key }
      })
    },

    selectedOptions() {
      const map = new Map(this.normalizedOptions.map(opt => [opt.value, opt]))

      const ordered = []
      for (const nV of this.normalizedValues) {
        const opt = map.get(nV)
        if (opt) ordered.push(opt)
      }

      return ordered
    },

    hasValue() {
      return this.selectedOptions?.length
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        document.addEventListener('click', this.onDocClick, true)
      } else {
        document.removeEventListener('click', this.onDocClick, true)
      }
    },
  },
  methods: {
    resolve(obj, resolver) {
      if (typeof resolver === 'function') return resolver(obj)
      if (!resolver) return obj

      const path = String(resolver).split('.')
      let cur = obj
      for (const k of path) {
        if (cur == null) return undefined
        cur = cur[k]
      }
      return cur
    },

    isSelected(option) {
      return this.getSelectedIndex(option) >= 0
    },

    getSelectedIndex(option) {
      const resolvedValue = this.valueResolver(option)

      return this.normalizedValues.findIndex(nV => nV === resolvedValue)
    },

    removeByIndex(arr, index) {
      if (!Array.isArray(arr)) return []
      if (index < 0 || index >= arr.length) return arr.slice()

      return [...arr.slice(0, index), ...arr.slice(index + 1)]
    },

    isOptionDisabled(option) {
      return !!this.resolve(option, this.optionDisabled)
    },

    toggle() {
      if (this.disabled) return
      this.isOpen ? this.close() : this.open()
    },

    open() {
      if (this.disabled) return
      this.isOpen = true
    },

    close() {
      this.isOpen = false
    },

    emitValue(option) {
      const resolvedValue = this.valueResolver(option)

      let value

      if (this.multiple) {
        const selectedIdx = this.getSelectedIndex(option)
        if (selectedIdx >= 0) {
          value = this.removeByIndex(this.normalizedValues, selectedIdx)
        } else value = [...this.normalizedValues, resolvedValue]
      } else value = resolvedValue

      this.$emit('input', value)
      this.$emit('change', { value, option })
    },

    setOption(option) {
      if (this.isOptionDisabled(option)) return
      this.emitValue(option)
      if (this.closeOnSelect) this.close()
    },

    onDocClick(e) {
      const root = this.$refs.root
      if (!root) return
      if (!root.contains(e.target)) this.close()
    },

    onKeydown(e) {
      if (this.disabled) return

      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        this.open()
        return
      }

      if (!this.isOpen) return

      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
      }
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.b-select
  position: relative
  display: inline-block
  width: 100%

  &__label
    font-size: 14px
    line-height: 1.3
    color: $dark-shades-black

  &__control
    display: flex
    align-items: center
    justify-content: space-between
    gap: 4px
    width: 100%
    border: 1px solid #e7e7e8
    border-radius: 6px
    color: #293045
    font-size: 14px
    line-height: 1.286
    padding: 11px 24px
    margin-top: 8px
    transition: border-color .2s

    &--error
      border-color: $error

    &:focus
      border-color: #00c8d7

  &__label
    font-size: 14px
    color: #293045

  &__chevron
    color: #C8C8C8
    transition: all .2s

  &__placeholder
    font-size: 14px
    line-height: normal
    color: $gray-text

  &__values
    display: flex
    gap: 8px
    width: calc(100% - 20px)

  &.is-open
    .b-select__chevron
      transform: rotate(180deg)
      transform-style: preserve-3d

  &.is-disabled
      pointer-events: none

  &__dropdown
    display: flex
    flex-direction: column
    position: absolute
    top: calc(100% - 2px)
    left: -1px
    z-index: 50
    padding: 8px 0 16px
    background: #fff
    border: 1px solid #e7e7e8
    border-radius: 0 0 3px 3px
    box-shadow: 0 5px 20px rgba(0, 0, 0, .1)
    width: calc(100% + 2px)
    max-height: 324px
    overflow-y: auto

  &__option, &__empty
    font-size: 14px
    line-height: 1.2
    padding: 8px 24px

  &__option
    cursor: pointer

  &__required
    font-size: 13px

  &__error, &__hint
    position: absolute
    bottom: -15px
    font-size: 12px
    line-height: 1

  &__required, &__error
    color: $error
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/lk/form/BaseSelect.vue